/** @format */
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { assetWidthCss, type Brand } from "@question-bank/shared";
import { getAssetUrl } from "../../../api/questions";
import { MathText } from "../../../components/MathText";
import type { AnswerLinesConfig, PdfQuestion } from "../types";
import {
	ANSWER_LINE_HEIGHT,
	ANSWER_LINES_MARGIN_RIGHT,
	ANSWER_LINES_MARGIN_TOP,
	answerLineCount,
	blockGapAbove,
	BODY_FONT_SIZE,
	BODY_LINE_HEIGHT,
	COLOR_ANSWER_LINE,
	COLOR_LABEL,
	COLOR_MISSING,
	COLOR_TEXT,
	FOOTNOTE_FONT_SIZE,
	formatMarks,
	HIERARCHY_INDENT_PER_LEVEL,
	LABEL_WIDTH,
	MARKS_WIDTH,
	NUMBER_FONT_SIZE,
	NUMBER_WIDTH,
	PART_MARGIN_BOTTOM_LAST,
	PART_MARGIN_BOTTOM,
	partLabel,
	pt,
	SVG_DISPLAY_WIDTH,
	TEXT_PADDING_RIGHT,
} from "../layout";

interface QuestionContentProps {
	question: PdfQuestion;
	paperNumber: string;
	precedingLabels: string[];
	inlineParentLabel?: string;
	isLastPart: boolean;
	brand: Brand;
	answerLines: AnswerLinesConfig;
}

const answerLinesGroupStyle: CSSProperties = {
	marginRight: pt(ANSWER_LINES_MARGIN_RIGHT),
};
const trailingAnswerLinesStyle: CSSProperties = {
	...answerLinesGroupStyle,
	marginTop: pt(ANSWER_LINES_MARGIN_TOP),
	marginBottom: pt(4),
};

const answerLineStyle: CSSProperties = {
	height: pt(ANSWER_LINE_HEIGHT),
	borderBottom: `1pt dotted ${COLOR_ANSWER_LINE}`,
};

const AnswerLinesGroup = ({ lines, style }: { lines: number; style: CSSProperties }) => (
	<div style={style}>
		{Array.from({ length: lines }).map((_, index) => (
			<div key={`line-${index}`} data-testid="answer-line" style={answerLineStyle} />
		))}
	</div>
);

/**
 * HTML twin of pdf/QuestionContent [QB2-122]: one part row on the on screen pdf view, laid out
 * from the shared "layout" constants so the page and the export cannot drift. *
 */

export const QuestionContent = ({
	question,
	paperNumber,
	precedingLabels,
	inlineParentLabel,
	isLastPart,
	brand,
	answerLines,
}: QuestionContentProps) => {
	const { t } = useTranslation();
	const hasInlineAnswerLines = question.blocks.some((block) => block.kind === "answerLines");
	const trailingLineCount =
		answerLines.enabled && !hasInlineAnswerLines && !question.isParentHeader
			? (question.answerLines ?? answerLines.totalLines ?? answerLineCount(question.maxMarks))
			: 0;
	const indentLevels = inlineParentLabel === undefined ? question.depth : question.depth - 1;
	const depthIndent = indentLevels * HIERARCHY_INDENT_PER_LEVEL;
	return (
		<div
			style={{
				marginBottom: pt(isLastPart ? PART_MARGIN_BOTTOM_LAST : PART_MARGIN_BOTTOM),
				marginLeft: depthIndent > 0 ? pt(depthIndent) : undefined,
			}}
		>
			<div className="flex items-start">
				<span
					style={{
						width: pt(NUMBER_WIDTH),
						flexShrink: 0,
						fontSize: pt(NUMBER_FONT_SIZE),
						fontWeight: "bold",
						color: COLOR_TEXT,
					}}
				>
					{paperNumber}
				</span>
				{inlineParentLabel !== undefined && (
					<span
						style={{
							width: pt(HIERARCHY_INDENT_PER_LEVEL),
							flexShrink: 0,
							fontSize: pt(BODY_FONT_SIZE),
							fontStyle: "italic",
							color: COLOR_LABEL,
						}}
					>
						{inlineParentLabel}
					</span>
				)}
				<span
					style={{
						width: pt(LABEL_WIDTH),
						flexShrink: 0,
						fontSize: pt(BODY_FONT_SIZE),
						fontStyle: "italic",
						color: COLOR_LABEL,
					}}
				>
					{partLabel(question.displayLabel, precedingLabels)}
				</span>
				{!question.isParentHeader && (
					<div style={{ flex: 1, minWidth: 0, paddingRight: pt(TEXT_PADDING_RIGHT) }}>
						{question.blocks.length === 0 ? (
							<span
								style={{
									paddingRight: pt(TEXT_PADDING_RIGHT),
									color: COLOR_MISSING,
									fontSize: pt(FOOTNOTE_FONT_SIZE),
								}}
							>
								{t("pdfExport.questionContentMissing")}
							</span>
						) : (
							question.blocks.map((block, index) => {
								// BLOCK_GAP applies between blocks only, every block after the first carries it as
								// a top margin, so the first sits flush under the header line.
								const gapStyle: CSSProperties =
									index > 0 ? { marginTop: pt(blockGapAbove(question.blocks[index - 1], block)) } : {};
								if (block.kind === "text") {
									return (
										<div
											key={`block-${index}`}
											style={{
												fontSize: pt(BODY_FONT_SIZE),
												lineHeight: BODY_LINE_HEIGHT,
												color: COLOR_TEXT,
												whiteSpace: "pre-wrap",
												overflowWrap: "break-word",
												...gapStyle,
											}}
										>
											<MathText text={block.text} />
										</div>
									);
								}
								if (block.kind === "answerLines") {
									return (
										<AnswerLinesGroup
											key={`block-${index}`}
											lines={block.lines}
											style={{ ...answerLinesGroupStyle, ...gapStyle }}
										/>
									);
								}
								if (!block.assetKey) {
									return null;
								}

								const isSvgAsset = /\.svg($|[?#])/i.test(block.assetKey);
								// Mirrors pdf/QuestionContent: an authored percentage width wins
								// over the vector default, and stays responsive via maxWidth.
								const authoredWidth = assetWidthCss(block.widthPercent);
								return (
									<img
										key={`block-${index}`}
										src={getAssetUrl(brand, block.assetKey)}
										alt={block.alt ?? `Question ${question.displayLabel}`}
										style={{
											display: "block",
											marginLeft: "auto",
											marginRight: "auto",
											...(authoredWidth
												? { width: authoredWidth }
												: isSvgAsset
													? { width: pt(SVG_DISPLAY_WIDTH) }
													: {}),
											maxWidth: "100%",
											...gapStyle,
										}}
									/>
								);
							})
						)}
						{trailingLineCount > 0 && <AnswerLinesGroup lines={trailingLineCount} style={trailingAnswerLinesStyle} />}
					</div>
				)}
				{question.maxMarks !== null && (
					<span
						style={{
							width: pt(MARKS_WIDTH),
							flexShrink: 0,
							textAlign: "right",
							fontSize: pt(BODY_FONT_SIZE),
							color: COLOR_TEXT,
						}}
					>
						{formatMarks(question.maxMarks)}
					</span>
				)}
			</div>
		</div>
	);
};
